// Double-checks an FTC report before it's generated so swapped / wrong fields get caught.
const STATES = new Set('AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC PR VI GU AS MP'.split(' '));
const digits = (v) => String(v || '').replace(/\D/g, '');
const PHONE_LIKE = /^\D*\(?\d{3}\)?[\s.-]*\d{3}[\s.-]*\d{4}\D*$/;
const SSN_LIKE = /^\D*\d{3}[\s-]?\d{2}[\s-]?\d{4}\D*$/;

const parseDate = (v) => {
  const s = String(v || '').trim();
  let m = s.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/);
  if (m) return new Date(+m[1], +m[2] - 1, +(m[3] || 1));
  m = s.match(/^(\d{1,2})[/-](?:(\d{1,2})[/-])?(\d{4})$/);
  if (m) return new Date(+m[3], +m[1] - 1, +(m[2] || 1));
  return null;
};

export function checkClient(ci = {}) {
  const out = [];
  const add = (field, msg, level = 'error') => out.push({ field, msg, level });
  const name = (ci.name || '').trim();
  const addr = (ci.address || '').trim();
  const phoneD = digits(ci.phone);
  const ssnD = digits(ci.ssn);

  if (!name) add('Name', 'Name is empty.');
  else if (/\d/.test(name)) add('Name', `Name "${name}" has numbers in it.`);

  if (!addr) add('Address', 'Street address is empty.');
  else if (PHONE_LIKE.test(addr)) add('Address', `Address "${addr}" is a phone number, not a street address.`);
  else if (SSN_LIKE.test(addr)) add('Address', `Address "${addr}" looks like a Social Security number.`);
  else if (!/[a-z]/i.test(addr)) add('Address', `Address "${addr}" has no street name.`);
  else if (addr.includes('@')) add('Address', `Address "${addr}" contains an email.`);
  else if (!/^\s*\d+/.test(addr) && !/p\.?\s*o\.?\s*box/i.test(addr)) add('Address', `Address "${addr}" has no house number.`, 'warning');
  if (phoneD.length >= 10 && digits(addr).includes(phoneD.slice(-10))) add('Phone', `The phone digits come from the address "${addr}" — the phone box has the wrong number.`);

  if (!(ci.city || '').trim()) add('City', 'City is empty.');
  else if (/\d/.test(ci.city)) add('City', `City "${ci.city}" has numbers in it.`);
  if (!STATES.has(String(ci.state || '').trim().toUpperCase())) add('State', `State "${ci.state || ''}" isn't a 2-letter US state.`);
  if (!/^\d{5}(-\d{4})?$/.test(String(ci.zip_code || '').trim())) add('ZIP', `ZIP "${ci.zip_code || ''}" should be 5 digits.`);

  const p10 = phoneD.length === 11 && phoneD[0] === '1' ? phoneD.slice(1) : phoneD;
  if (!phoneD) add('Phone', 'Phone is empty.', 'warning');
  else if (p10.length !== 10) add('Phone', `Phone "${ci.phone}" has ${phoneD.length} digits — a US phone has 10.${phoneD.length === 9 ? ' That is the length of a Social Security number.' : ''}`);
  else if (/[01]/.test(p10[0]) || /[01]/.test(p10[3])) add('Phone', `Phone "${ci.phone}" isn't a real US number (area codes and prefixes never start with 0 or 1). It may be an SSN or account number in the wrong box.`);
  if (ssnD && phoneD && (phoneD.includes(ssnD) || ssnD.includes(p10))) add('Phone', 'Phone and SSN contain the same digits — one of them is in the wrong box.');

  if (ssnD) {
    if (ssnD.length !== 9) add('SSN', `SSN has ${ssnD.length} digits — it should have 9.${ssnD.length === 10 ? ' That is the length of a phone number.' : ''}`);
    else if (/^(000|666|9)/.test(ssnD) || ssnD.slice(3, 5) === '00' || ssnD.slice(5) === '0000') add('SSN', "SSN isn't a valid number.");
  }
  if (ci.email && !/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(ci.email.trim())) add('Email', `Email "${ci.email}" isn't a valid email.`);
  if (ci.dob) {
    const d = parseDate(ci.dob);
    const age = d ? (Date.now() - d.getTime()) / 31557600000 : null;
    if (!d || Number.isNaN(age)) add('Date of birth', `Date of birth "${ci.dob}" isn't a date.`);
    else if (age < 18 || age > 110) add('Date of birth', `Date of birth "${ci.dob}" makes the client ${Math.floor(age)} years old.`);
  }
  return out;
}

export function checkSections(sections = []) {
  const out = [];
  const seen = new Map();
  sections.forEach((s) => {
    const where = `Section ${s.ftc_section}`;
    if (!s.fraud_type) out.push({ field: where, msg: `${where} has no fraud type picked.`, level: 'error' });
    (s.accounts || []).forEach((a, i) => {
      const nm = (a.creditor_name || '').trim();
      const tag = `${where}, account ${i + 1}${nm ? ` (${nm})` : ''}`; // used as the field label
      if (!nm) out.push({ field: tag, msg: `Creditor name is empty.`, level: 'error' });
      else if (!/[a-z]/i.test(nm) || PHONE_LIKE.test(nm) || SSN_LIKE.test(nm)) out.push({ field: tag, msg: `Creditor name "${nm}" is only numbers.`, level: 'error' });
      const bal = String(a.balance ?? '').replace(/[$,\s]/g, '');
      if (!bal || Number(bal) === 0) out.push({ field: tag, msg: 'Amount is empty or $0.', level: 'warning' });
      else if (Number.isNaN(Number(bal))) out.push({ field: tag, msg: `Amount "${a.balance}" isn't a number.`, level: 'error' });
      if (a.date_opened) {
        const d = parseDate(a.date_opened);
        if (!d) out.push({ field: tag, msg: `Date "${a.date_opened}" isn't a date.`, level: 'warning' });
        else if (d > new Date()) out.push({ field: tag, msg: `Date ${a.date_opened} is in the future.`, level: 'error' });
        else if (d.getFullYear() < 1960) out.push({ field: tag, msg: `Date ${a.date_opened} is too old.`, level: 'warning' });
      }
      const key = `${nm.toLowerCase()}|${digits(a.account_number)}|${a.date_opened || ''}|${bal}`;
      if (nm && seen.has(key)) out.push({ field: tag, msg: `Same account is already listed in ${seen.get(key)}.`, level: 'warning' });
      else seen.set(key, where);
    });
  });
  return out;
}

export function checkStatement(text, clientName, sections = []) {
  const t = String(text || '').trim();
  if (text == null) return [];
  const out = [];
  const add = (msg, level = 'error') => out.push({ field: 'Personal statement', msg, level });
  if (t.length < 40) { add('Personal statement is empty or too short.'); return out; }
  if (/\[[^\]]{2,30}\]|\{\{|client name|lorem ipsum/i.test(t)) add('Personal statement still has a placeholder like [NAME] in it.');
  const said = t.match(/[Mm]y name is ([A-Z][A-Za-z'-]+(?: [A-Z][A-Za-z'-]+){0,3})/);
  const parts = (clientName || '').toLowerCase().split(/[\s,]+/).filter((w) => w.length > 1 && !/^(jr|sr|ii|iii|iv)\.?$/.test(w));
  const saidWords = said ? said[1].toLowerCase().split(/\s+/) : [];
  if (said && parts.length && !saidWords.some((w) => parts.includes(w))) add(`Personal statement says "my name is ${said[1]}" but the client is ${clientName}.`);
  const names = sections.flatMap((s) => (s.accounts || []).map((a) => (a.creditor_name || '').trim())).filter(Boolean);
  const word = (n) => n.toLowerCase().split(/\s+/).find((w) => w.length > 2) || n.toLowerCase();
  if (names.length && !names.some((n) => t.toLowerCase().includes(word(n))) && !/account|creditor|loan|card/i.test(t)) add("Personal statement doesn't mention any of the accounts.", 'warning');
  return out;
}

export const checkFtcReport = (clientInfo, sections, statement) => [...checkClient(clientInfo), ...checkSections(sections), ...checkStatement(statement, clientInfo?.name, sections)];
